import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiDollarSign, FiTruck, FiMapPin, FiCalendar, FiTrendingUp } from 'react-icons/fi';
import api from '@/services/api';
import { formatCurrency } from '@/utils/formatters';
import LoadingSpinner from '@/components/common/LoadingSpinner';

const DriverEarnings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [earnings, setEarnings] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    loadEarnings();
  }, []);

  const loadEarnings = async () => {
    try {
      const response = await api.get('/driver/earnings');
      setEarnings(response.data);
    } catch (err) {
      setError('Erro ao carregar ganhos');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center text-white">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#1A1A1A] to-[#DC143C] border-b border-gray-700">
        <div className="max-w-lg mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/entregador')} className="p-2 hover:bg-white/10 rounded-full">
              <FiArrowLeft size={20} />
            </button>
            <h1 className="text-xl font-bold">Meus Ganhos</h1>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gradient-to-br from-green-600 to-green-800 rounded-xl p-4 text-center">
            <FiDollarSign className="mx-auto text-2xl mb-2" />
            <p className="text-2xl font-bold">{formatCurrency(earnings?.total_earnings || 0)}</p>
            <p className="text-white/70 text-sm">Total Ganho</p>
          </div>
          <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl p-4 text-center">
            <FiTruck className="mx-auto text-2xl mb-2" />
            <p className="text-2xl font-bold">{earnings?.total_deliveries || 0}</p>
            <p className="text-white/70 text-sm">Entregas</p>
          </div>
          <div className="bg-gradient-to-br from-purple-600 to-purple-800 rounded-xl p-4 text-center">
            <FiMapPin className="mx-auto text-2xl mb-2" />
            <p className="text-2xl font-bold">{earnings?.total_km || 0} km</p>
            <p className="text-white/70 text-sm">Total Rodados</p>
          </div>
          <div className="bg-gradient-to-br from-orange-600 to-orange-800 rounded-xl p-4 text-center">
            <FiTrendingUp className="mx-auto text-2xl mb-2" />
            <p className="text-2xl font-bold">{formatCurrency(earnings?.avg_per_delivery || 0)}</p>
            <p className="text-white/70 text-sm">Média/Entrega</p>
          </div>
        </div>

        {/* Period Summary */}
        <div className="bg-gray-800 rounded-xl p-4">
          <h2 className="font-semibold mb-4 flex items-center gap-2">
            <FiCalendar className="text-[#DC143C]" /> Resumo por Período
          </h2>
          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-gray-700/50 rounded-lg">
              <span className="text-gray-300">Hoje</span>
              <span className="font-bold text-green-400">{formatCurrency(earnings?.earnings_today || 0)}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-700/50 rounded-lg">
              <span className="text-gray-300">Esta Semana</span>
              <span className="font-bold text-green-400">{formatCurrency(earnings?.earnings_week || 0)}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-700/50 rounded-lg">
              <span className="text-gray-300">Este Mês</span>
              <span className="font-bold text-green-400">{formatCurrency(earnings?.earnings_month || 0)}</span>
            </div>
          </div>
        </div>

        {/* History */}
        <div className="bg-gray-800 rounded-xl p-4">
          <h2 className="font-semibold mb-4">Histórico de Entregas</h2>
          
          {earnings?.history?.length === 0 ? (
            <p className="text-gray-400 text-center py-8">Nenhuma entrega realizada ainda</p>
          ) : (
            <div className="space-y-3">
              {earnings?.history?.map((delivery) => (
                <div key={delivery.id} className="p-3 bg-gray-700/50 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{delivery.store_name}</p>
                      <p className="text-sm text-gray-400">{delivery.delivery_address}</p>
                      <div className="flex gap-3 mt-1 text-xs text-gray-500">
                        <span>{delivery.date}</span>
                        <span>{delivery.km_traveled} km</span>
                      </div>
                    </div>
                    <span className="font-bold text-green-400">{formatCurrency(delivery.amount)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default DriverEarnings;
